import React, { useState, useEffect } from 'react';

const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState({
    fontSize: 'normal',
    theme: 'light',
    focusIndicator: true,
    reducedMotion: false,
    cursorSize: 'normal',
    lineHeight: 'normal',
    textAlignment: 'left',
    hideImages: false,
    highlightLinks: false,
    showTooltips: true,
    textSpacing: 'normal'
  });

  // Apply accessibility settings to document
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;

    // Font size
    root.style.fontSize = settings.fontSize === 'large' ? '18px' :
      settings.fontSize === 'small' ? '14px' : '16px';

    // Theme - Apply to both html and body for better coverage
    root.classList.remove('high-contrast', 'dark-mode');
    body.classList.remove('high-contrast', 'dark-mode');
    if (settings.theme === 'high-contrast') {
      root.classList.add('high-contrast');
      body.classList.add('high-contrast');
      console.log('Applied high-contrast theme');
    } else if (settings.theme === 'dark') {
      root.classList.add('dark-mode');
      body.classList.add('dark-mode');
      console.log('Applied dark-mode theme');
    } else {
      console.log('Applied light theme');
    }

    // Focus indicator
    if (settings.focusIndicator) {
      root.classList.add('focus-indicator');
    } else {
      root.classList.remove('focus-indicator');
    }

    // Reduced motion
    if (settings.reducedMotion) {
      root.classList.add('reduced-motion');
    } else {
      root.classList.remove('reduced-motion');
    }

    // Cursor size
    root.classList.remove('cursor-large', 'cursor-small');
    if (settings.cursorSize === 'large') {
      root.classList.add('cursor-large');
    } else if (settings.cursorSize === 'small') {
      root.classList.add('cursor-small');
    }

    // Line height
    root.style.lineHeight = settings.lineHeight === 'large' ? '1.8' :
      settings.lineHeight === 'small' ? '1.2' : '1.5';

    // Text alignment
    root.style.textAlign = settings.textAlignment;

    // Hide images
    if (settings.hideImages) {
      root.classList.add('hide-images');
    } else {
      root.classList.remove('hide-images');
    }

    // Highlight links
    if (settings.highlightLinks) {
      root.classList.add('highlight-links');
    } else {
      root.classList.remove('highlight-links');
    }

    // Text spacing
    root.classList.remove('text-spacing-large', 'text-spacing-small');
    if (settings.textSpacing === 'large') {
      root.classList.add('text-spacing-large');
    } else if (settings.textSpacing === 'small') {
      root.classList.add('text-spacing-small');
    }

    // Tooltips
    root.classList.remove('show-tooltips', 'hide-tooltips');
    if (settings.showTooltips) {
      root.classList.add('show-tooltips');
    } else {
      root.classList.add('hide-tooltips');
    }
  }, [settings]);

  // Handle body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const toggleSetting = (setting) => {
    setSettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  };

  const setFontSize = (size) => {
    setSettings(prev => ({
      ...prev,
      fontSize: size
    }));
  };

  const toggleDrawer = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      {/* Fixed Accessibility Icon */}
      <button
        onClick={toggleDrawer}
        className={`accessibility-button fixed right-4 top-1/2 transform -translate-y-1/2 z-50 w-14 h-14 rounded-full shadow-lg transition-all duration-300 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${isOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'
          }`}
        aria-label="Open accessibility options"
        title="Accessibility Options"
        style={{
          background: 'linear-gradient(135deg, #006B7A 0%, #004952 50%, #00272A 100%)',
          border: '3px solid #CBF9FF'
        }}
      >
        <svg
          className="w-8 h-8 mx-auto"
          fill="white"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          {/* Universal Accessibility Symbol - Stylized Human Figure */}
          {/* Simple Human Figure Icon */}
          <circle cx="12" cy="8" r="3" fill="white" />
          <path d="M7 20c0-2.5 2.2-4.5 5-4.5s5 2 5 4.5" stroke="white" strokeWidth="2" fill="none" />
          <path d="M12 11v6" stroke="white" strokeWidth="2" />
          <path d="M9 14h6" stroke="white" strokeWidth="2" />
        </svg>
      </button>

      {/* Accessibility Drawer */}
      <div className={`accessibility-drawer fixed right-0 top-0 h-full w-80 bg-white shadow-2xl transform transition-transform duration-300 z-40 flex flex-col ${isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}>
        {/* Drawer Header */}
        <div className="bg-primary-600 text-white p-4 flex justify-between items-center">
          <h2 className="text-lg font-semibold">Accessibility Options</h2>
          <button
            onClick={toggleDrawer}
            className="accessibility-link text-white hover:text-primary-200 transition-colors duration-200"
            aria-label="Close accessibility options"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Drawer Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Text Size Options */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Text Size</h3>
            <div className="flex justify-center space-x-2">
              <button
                onClick={() => setFontSize('small')}
                className={`px-4 py-2 rounded-lg border-2 transition-colors duration-200 ${settings.fontSize === 'small'
                    ? 'bg-primary-600 border-primary-600 text-white'
                    : 'bg-white border-gray-300 text-gray-700 hover:border-primary-400'
                  }`}
                title="Decrease text size"
              >
                <span className="text-sm font-bold">A-</span>
              </button>
              <button
                onClick={() => setFontSize('normal')}
                className={`px-4 py-2 rounded-lg border-2 transition-colors duration-200 ${settings.fontSize === 'normal'
                    ? 'bg-primary-600 border-primary-600 text-white'
                    : 'bg-white border-gray-300 text-gray-700 hover:border-primary-400'
                  }`}
                title="Normal text size"
              >
                <span className="text-base font-bold">A</span>
              </button>
              <button
                onClick={() => setFontSize('large')}
                className={`px-4 py-2 rounded-lg border-2 transition-colors duration-200 ${settings.fontSize === 'large'
                    ? 'bg-primary-600 border-primary-600 text-white'
                    : 'bg-white border-gray-300 text-gray-700 hover:border-primary-400'
                  }`}
                title="Increase text size"
              >
                <span className="text-lg font-bold">A+</span>
              </button>
            </div>
          </div>

          {/* Theme Options */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Theme</h3>
            <div className="space-y-2">
              <button
                onClick={() => setSettings(prev => ({ ...prev, theme: 'light' }))}
                className={`w-full text-left px-4 py-2 rounded-lg border transition-colors duration-200 ${settings.theme === 'light'
                    ? 'bg-primary-100 border-primary-500 text-primary-700'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                  }`}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 rounded-full bg-white border-2 border-gray-400"></div>
                  <span>Light Theme</span>
                </div>
              </button>
              <button
                onClick={() => setSettings(prev => ({ ...prev, theme: 'dark' }))}
                className={`w-full text-left px-4 py-2 rounded-lg border transition-colors duration-200 ${settings.theme === 'dark'
                    ? 'bg-primary-100 border-primary-500 text-primary-700'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                  }`}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 rounded-full bg-gray-800 border-2 border-gray-400"></div>
                  <span>Dark Theme</span>
                </div>
              </button>
              <button
                onClick={() => setSettings(prev => ({ ...prev, theme: 'high-contrast' }))}
                className={`w-full text-left px-4 py-2 rounded-lg border transition-colors duration-200 ${settings.theme === 'high-contrast'
                    ? 'bg-primary-100 border-primary-500 text-primary-700'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                  }`}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 rounded-full bg-black border-2 border-white"></div>
                  <span>High Contrast</span>
                </div>
              </button>
            </div>
          </div>

          {/* Cursor Size */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Cursor Size</h3>
            <div className="space-y-2">
              <button
                onClick={() => setSettings(prev => ({ ...prev, cursorSize: 'small' }))}
                className={`w-full text-left px-4 py-2 rounded-lg border transition-colors duration-200 ${settings.cursorSize === 'small'
                    ? 'bg-primary-100 border-primary-500 text-primary-700'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                  }`}
              >
                <span>Small Cursor</span>
              </button>
              <button
                onClick={() => setSettings(prev => ({ ...prev, cursorSize: 'normal' }))}
                className={`w-full text-left px-4 py-2 rounded-lg border transition-colors duration-200 ${settings.cursorSize === 'normal'
                    ? 'bg-primary-100 border-primary-500 text-primary-700'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                  }`}
              >
                <span>Normal Cursor</span>
              </button>
              <button
                onClick={() => setSettings(prev => ({ ...prev, cursorSize: 'large' }))}
                className={`w-full text-left px-4 py-2 rounded-lg border transition-colors duration-200 ${settings.cursorSize === 'large'
                    ? 'bg-primary-100 border-primary-500 text-primary-700'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                  }`}
              >
                <span>Large Cursor</span>
              </button>
            </div>
          </div>

          {/* Line Height */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Line Height</h3>
            <div className="space-y-2">
              <button
                onClick={() => setSettings(prev => ({ ...prev, lineHeight: 'small' }))}
                className={`w-full text-left px-4 py-2 rounded-lg border transition-colors duration-200 ${settings.lineHeight === 'small'
                    ? 'bg-primary-100 border-primary-500 text-primary-700'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                  }`}
              >
                <span>Tight</span>
              </button>
              <button
                onClick={() => setSettings(prev => ({ ...prev, lineHeight: 'normal' }))}
                className={`w-full text-left px-4 py-2 rounded-lg border transition-colors duration-200 ${settings.lineHeight === 'normal'
                    ? 'bg-primary-100 border-primary-500 text-primary-700'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                  }`}
              >
                <span>Normal</span>
              </button>
              <button
                onClick={() => setSettings(prev => ({ ...prev, lineHeight: 'large' }))}
                className={`w-full text-left px-4 py-2 rounded-lg border transition-colors duration-200 ${settings.lineHeight === 'large'
                    ? 'bg-primary-100 border-primary-500 text-primary-700'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                  }`}
              >
                <span>Loose</span>
              </button>
            </div>
          </div>

          {/* Text Alignment */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Text Alignment</h3>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => setSettings(prev => ({ ...prev, textAlignment: 'left' }))}
                className={`px-3 py-2 rounded-lg border transition-colors duration-200 ${settings.textAlignment === 'left'
                    ? 'bg-primary-100 border-primary-500 text-primary-700'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                  }`}
                title="Left align"
              >
                <svg className="w-4 h-4 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h6a1 1 0 110 2H4a1 1 0 01-1-1zM3 16a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" />
                </svg>
              </button>
              <button
                onClick={() => setSettings(prev => ({ ...prev, textAlignment: 'center' }))}
                className={`px-3 py-2 rounded-lg border transition-colors duration-200 ${settings.textAlignment === 'center'
                    ? 'bg-primary-100 border-primary-500 text-primary-700'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                  }`}
                title="Center align"
              >
                <svg className="w-4 h-4 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM7 10a1 1 0 011-1h4a1 1 0 110 2H8a1 1 0 01-1-1zM3 16a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" />
                </svg>
              </button>
              <button
                onClick={() => setSettings(prev => ({ ...prev, textAlignment: 'right' }))}
                className={`px-3 py-2 rounded-lg border transition-colors duration-200 ${settings.textAlignment === 'right'
                    ? 'bg-primary-100 border-primary-500 text-primary-700'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                  }`}
                title="Right align"
              >
                <svg className="w-4 h-4 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM11 10a1 1 0 011-1h4a1 1 0 110 2h-4a1 1 0 01-1-1zM3 16a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" />
                </svg>
              </button>
            </div>
          </div>

          {/* Text Spacing */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Text Spacing</h3>
            <div className="space-y-2">
              <button
                onClick={() => setSettings(prev => ({ ...prev, textSpacing: 'small' }))}
                className={`w-full text-left px-4 py-2 rounded-lg border transition-colors duration-200 ${settings.textSpacing === 'small'
                    ? 'bg-primary-100 border-primary-500 text-primary-700'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                  }`}
              >
                <span>Tight Spacing</span>
              </button>
              <button
                onClick={() => setSettings(prev => ({ ...prev, textSpacing: 'normal' }))}
                className={`w-full text-left px-4 py-2 rounded-lg border transition-colors duration-200 ${settings.textSpacing === 'normal'
                    ? 'bg-primary-100 border-primary-500 text-primary-700'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                  }`}
              >
                <span>Normal Spacing</span>
              </button>
              <button
                onClick={() => setSettings(prev => ({ ...prev, textSpacing: 'large' }))}
                className={`w-full text-left px-4 py-2 rounded-lg border transition-colors duration-200 ${settings.textSpacing === 'large'
                    ? 'bg-primary-100 border-primary-500 text-primary-700'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                  }`}
              >
                <span>Loose Spacing</span>
              </button>
            </div>
          </div>

          {/* Visual Options */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Visual Options</h3>
            <div className="space-y-3">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.focusIndicator}
                  onChange={() => toggleSetting('focusIndicator')}
                  className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-gray-700">Enhanced Focus Indicators</span>
              </label>

              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.hideImages}
                  onChange={() => toggleSetting('hideImages')}
                  className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-gray-700">Hide Images</span>
              </label>

              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.highlightLinks}
                  onChange={() => toggleSetting('highlightLinks')}
                  className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-gray-700">Highlight Links</span>
              </label>

              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.showTooltips}
                  onChange={() => toggleSetting('showTooltips')}
                  className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-gray-700">Show Tooltips</span>
              </label>

              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.reducedMotion}
                  onChange={() => toggleSetting('reducedMotion')}
                  className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-gray-700">Reduce Motion</span>
              </label>
            </div>
          </div>

          {/* Quick Actions */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Quick Actions</h3>
            <div className="space-y-2">
              <button
                onClick={() => {
                  const mainContent = document.getElementById('main-content');
                  if (mainContent) {
                    mainContent.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="w-full text-left px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200"
              >
                Skip to Main Content
              </button>

              <button
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="w-full text-left px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200"
              >
                Back to Top
              </button>
            </div>
          </div>

          {/* Reset Button */}
          <div className="pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                setSettings({
                  fontSize: 'normal',
                  theme: 'light',
                  focusIndicator: true,
                  reducedMotion: false,
                  cursorSize: 'normal',
                  lineHeight: 'normal',
                  textAlignment: 'left',
                  hideImages: false,
                  highlightLinks: false,
                  showTooltips: true,
                  textSpacing: 'normal'
                });
              }}
              className="w-full px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors duration-200"
            >
              Reset All Settings
            </button>
          </div>
        </div>
      </div>

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-25 z-30"
          onClick={toggleDrawer}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default AccessibilityWidget;
